import { AlertCircle, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

import { ChannelSectionHeader } from '@/components/results/ChannelSectionHeader';
import { SoftCard } from '@/components/ui/soft-card';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { useShop } from '@/hooks/useShop';
import { DISCONNECTED_THRESHOLD_DAYS, daysSince } from '@/lib/connection-state';

const numberFormatter = new Intl.NumberFormat('en-GB');

/**
 * Per-channel block for sync-only destinations: the channel where unify
 * pushes inventory but does not get product-level performance data back
 * (e.g. Meta Commerce). Mirrors the Lovable design — a green sync card on
 * the left and a muted "why no views?" explainer on the right pointing at
 * the upstream platform's own analytics.
 */
export function ChannelSyncOnlyResults({ channel, platformLabel, platformUrl }) {
	const { shop } = useShop();
	const { summary } = useInventoryPageSummary(shop?.id, channel.id);
	const total = summary?.totalItems ?? 0;
	const inStock = summary?.inStockItems ?? 0;
	const daysSinceLastSync = daysSince(summary?.source?.lastSyncAt);
	const isStale = daysSinceLastSync != null && daysSinceLastSync >= DISCONNECTED_THRESHOLD_DAYS;
	const StatusIcon = isStale ? AlertTriangle : CheckCircle2;
	const tone = isStale ? 'bg-warning-soft text-warning-soft-foreground' : 'bg-success-soft text-success-soft-foreground';

	return (
		<section className="space-y-4">
			<ChannelSectionHeader channel={channel} sub={`Inventory syncing — performance lives in ${platformLabel}.`} />
			<div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-2">
				<SoftCard className="space-y-5 !p-5 md:!p-6">
					<div className="flex flex-wrap items-start gap-4">
						<div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${tone}`}>
							<StatusIcon className="h-5 w-5" />
						</div>
						<div className="min-w-0 flex-1">
							<div className="flex flex-wrap items-center gap-2">
								<h3 className="text-[15px] font-bold text-foreground md:text-base">Connected to {channel.title}</h3>
								<span
									className={`inline-flex items-center gap-1 rounded-full ${tone} px-2 py-0.5 text-[11px] font-semibold`}
								>
									{isStale ? null : (
										<span className="relative flex h-1.5 w-1.5">
											<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
											<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
										</span>
									)}
									{isStale ? 'Issues' : 'Live'}
								</span>
							</div>
							{isStale ? (
								<div className="mt-1 text-xs text-warning-soft-foreground">
									Inventory hasn&rsquo;t synced in {daysSinceLastSync} days — {platformLabel} may be showing stale stock.
								</div>
							) : null}
							<div className="mt-1 text-xs tabular-nums text-muted-foreground">
								<span className="font-semibold text-foreground">{numberFormatter.format(inStock)}</span> of{' '}
								{numberFormatter.format(total)} products syncing to {platformLabel}
							</div>
						</div>
					</div>
				</SoftCard>
				<SoftCard tone="muted">
					<div className="flex items-center gap-2 text-sm font-bold text-foreground">
						<AlertCircle className="h-4 w-4 text-primary" /> Why no views or sales here?
					</div>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						{platformLabel} doesn't share product-level performance data with partners. To see views, taps, reach and
						conversions for {channel.title}, check {platformLabel} directly — it's your source of truth for everything that
						happens after the sync.
					</p>
					<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
						<a
							href={platformUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
						>
							Open {platformLabel} <ExternalLink className="h-3.5 w-3.5" />
						</a>
					</p>
				</SoftCard>
			</div>
		</section>
	);
}
